import { ArrowRightOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SupportedLanguage } from '@/locales';
import { submitConsultation } from '@/service/api/contact';
import contactBanner from '@/assets/imgs/contact/contact-banner.png';
import mapBannerImage from '@/assets/imgs/contact/map-banner.png';
import locationIcon from '@/assets/imgs/contact/location.png';
import phoneIcon from '@/assets/imgs/contact/phone.png';
import faxIcon from '@/assets/imgs/contact/fax.png';
import emailIcon from '@/assets/imgs/contact/email.png'

const privacyPolicyUrls: Record<SupportedLanguage, string> = {
  'zh-CN': 'https://file.bilionpass.com/images/portal/privacy_zh.pdf',
  'en-US': 'https://file.bilionpass.com/images/portal/privacy_en.pdf',
  'ja-JP': 'https://file.bilionpass.com/images/portal/privacy_ja.pdf',
};

type ContactDetailProps = {
  icon: string | 'email';
  label: string;
  children: React.ReactNode;
};

function ContactDetail({ icon, label, children }: ContactDetailProps) {
  return (
    <div className="grid grid-cols-[70px_1fr] items-start gap-[20px]">
      <img src={icon} alt="" className="size-[70px]" />
      <div className="pt-[3px] text-[26px] leading-[1.42]">
        <p className="font-normal text-[#666]">{label}</p>
        <div className="mt-[4px] font-medium text-[#355f72]">{children}</div>
      </div>
    </div>
  );
}

type FormFieldProps = {
  id: string;
  label: string;
  placeholder: string;
  type?: 'email' | 'tel' | 'text';
};

function FormField({ id, label, placeholder, type = 'text' }: FormFieldProps) {
  return (
    <label htmlFor={id} className="flex flex-col gap-[8px] text-[26px] leading-[50px] text-[#121212]">
      <span>
        <span className="mr-[2px] text-[17px] text-[#00a0e9]">*</span>
        {label}
      </span>
      <input
        id={id}
        name={id}
        type={type}
        required
        placeholder={placeholder}
        className="h-[66px] rounded-[8px] border border-[#c6c6c6] bg-white px-[20px] text-[22px] leading-[50px] text-[#121212] outline-none transition-[border-color,box-shadow] placeholder:text-[#c4c4c4] focus:border-2 focus:border-[#00a0e9] focus:ring-4 focus:ring-[#00a0e9]/10"
      />
    </label>
  );
}

function ContactHero() {
  const { t } = useTranslation();

  return (
    <section className="relative h-[800px] overflow-hidden bg-[#dff1ff] text-center">
      <img
        src={contactBanner}
        alt=""
        className="absolute inset-0 size-full"
      />
      <div className="relative z-10 pt-[185px]">
        <h1 className="font-expose text-[116px] font-black capitalize leading-[0.973] text-[#00a0e9]">
          Contact Us
        </h1>
        <p className="mt-[24px] whitespace-pre-line font-douyin text-[30px] font-bold leading-[1.5] text-[#515151]">
          {t('contact.hero.description')}
        </p>
      </div>
    </section>
  );
}

function ContactForm() {
  const { t, i18n } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const language = (i18n.resolvedLanguage ?? i18n.language) as SupportedLanguage;
  const privacyPolicyUrl = privacyPolicyUrls[language] ?? privacyPolicyUrls['zh-CN'];

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const form = event.currentTarget;
    const formData = new FormData(form);

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const result = await submitConsultation({
        companyName: String(formData.get('company') ?? '').trim(),
        contactName: String(formData.get('contact-person') ?? '').trim(),
        phone: String(formData.get('phone') ?? '').trim(),
        email: String(formData.get('email') ?? '').trim(),
        content: String(formData.get('message') ?? '').trim(),
        agreePrivacy: formData.get('agreePrivacy') === 'on',
      });

      if (result.code !== 0) {
        throw new Error(result.msg || 'Unable to submit consultation');
      }

      form.reset();
      setSubmitStatus('success');
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative z-10 mx-auto mt-[-284px] grid min-h-[845px] w-[1510px] grid-cols-[557px_1fr] gap-[42px] rounded-[50px] bg-white p-[25px_50px_25px_20px] shadow-[0_20px_50px_rgba(0,0,0,0.05)]">
      <aside className="flex min-h-[795px] flex-col rounded-[30px] bg-[#f0f8ff] px-[40px] pb-[50px] pt-[59px]">
        <h2 className="font-douyin text-[50px] font-bold leading-none text-black">
          {t('contact.panel.title')}
        </h2>

        <address className="mt-[57px] flex flex-col gap-[34px] not-italic">
          <ContactDetail icon={locationIcon} label={t('contact.details.addressLabel')}>
            <p className="max-w-[389px] whitespace-pre-line">{t('contact.details.address')}</p>
          </ContactDetail>
          <ContactDetail icon={phoneIcon} label={t('contact.details.phoneLabel')}>
            <a href="tel:+81362057178" className="transition-colors hover:text-[#00a0e9]">
              03-6205-7178
            </a>
          </ContactDetail>
          <ContactDetail icon={faxIcon} label="FAX">
            <p>03-6205-7179</p>
          </ContactDetail>
          <ContactDetail icon={emailIcon} label="E-mail">
            <a href="mailto:bilionpass@jdxjp.net" className="lowercase transition-colors hover:text-[#00a0e9]">
              bilionpass@jdxjp.net
            </a>
          </ContactDetail>
        </address>
      </aside>

      <form
        className="flex min-w-0 flex-col pt-[17px]"
        onInvalid={event => {
          const field = event.target as HTMLInputElement | HTMLTextAreaElement;
          field.setCustomValidity(
            field.validity.typeMismatch
              ? t('contact.form.emailError')
              : t('contact.form.requiredError'),
          );
        }}
        onInput={event => {
          const field = event.target as HTMLInputElement | HTMLTextAreaElement;
          field.setCustomValidity('');
        }}
        onSubmit={handleSubmit}
      >
        <div className="grid grid-cols-2 gap-x-[36px] gap-y-[26px]">
          <FormField
            id="company"
            label={t('contact.form.companyLabel')}
            placeholder={t('contact.form.companyPlaceholder')}
          />
          <FormField
            id="contact-person"
            label={t('contact.form.personLabel')}
            placeholder={t('contact.form.personPlaceholder')}
          />
          <FormField
            id="phone"
            type="tel"
            label={t('contact.form.phoneLabel')}
            placeholder={t('contact.form.phonePlaceholder')}
          />
          <FormField
            id="email"
            type="email"
            label={t('contact.form.emailLabel')}
            placeholder={t('contact.form.emailPlaceholder')}
          />
          <label htmlFor="message" className="col-span-2 flex flex-col gap-[8px] text-[26px] leading-[50px] text-[#121212]">
            <span>
              <span className="mr-[2px] text-[17px] text-[#00a0e9]">*</span>
              {t('contact.form.messageLabel')}
            </span>
            <textarea
              id="message"
              name="message"
              required
              placeholder={t('contact.form.messagePlaceholder')}
              className="h-[180px] resize-none rounded-[8px] border border-[#c6c6c6] bg-white px-[20px] py-[8px] text-[22px] leading-[50px] text-[#121212] outline-none transition-[border-color,box-shadow] placeholder:text-[#c4c4c4] focus:border-2 focus:border-[#00a0e9] focus:ring-4 focus:ring-[#00a0e9]/10"
            />
          </label>
        </div>

        <label className="mt-[25px] flex cursor-pointer items-center justify-center gap-[10px] text-[22px] leading-[50px] text-black">
          <span className="relative size-[22px] shrink-0">
            <input
              name="agreePrivacy"
              type="checkbox"
              required
              className="peer absolute inset-0 size-full appearance-none rounded-[4px] border border-[#d9d9d9] bg-white checked:border-[#00a0e9] checked:bg-[#00a0e9] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#00a0e9]/20"
            />
            <svg
              viewBox="0 0 16 16"
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 hidden size-full p-[3px] text-white peer-checked:block"
            >
              <path
                d="m3.25 8.25 3 3 6.5-6.5"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </span>
          <span>
            {t('contact.form.agreementPrefix')}
            <a
              href={privacyPolicyUrl}
              target="_blank"
              rel="noreferrer"
              className="text-[#0036d7] underline-offset-4 hover:underline"
            >
              {t('contact.form.agreementLink')}
            </a>
            {t('contact.form.agreementSuffix')}
          </span>
        </label>

        <button
          type="submit"
          disabled={isSubmitting}
          className="group mx-auto mt-[24px] flex h-[60px] min-w-[240px] items-center justify-center overflow-hidden rounded-[85px] border-2 border-[#00a0e9] bg-[#00a0e9] px-[30px] text-[28px] font-medium leading-[1.5] text-white transition-[background-color,color] duration-[360ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:bg-white hover:text-[#00a0e9] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:duration-0"
        >
          <span className="flex items-center gap-[10px] whitespace-nowrap transition-transform duration-[360ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-[17px] motion-reduce:duration-0">
            {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')}
            <ArrowRightOutlined
              aria-hidden="true"
              className="text-[24px] opacity-100 transition-opacity duration-[120ms] group-hover:opacity-0 motion-reduce:duration-0"
            />
          </span>
        </button>
        {submitStatus !== 'idle' && (
          <p
            role={submitStatus === 'error' ? 'alert' : 'status'}
            className={`mt-[16px] text-center text-[20px] ${
              submitStatus === 'success' ? 'text-[#00a05a]' : 'text-[#e34242]'
            }`}
          >
            {t(`contact.form.${submitStatus === 'success' ? 'submitSuccess' : 'submitError'}`)}
          </p>
        )}
      </form>
    </section>
  );
}

function MapBanner() {
  const { t } = useTranslation();

  return (
    <section className="relative mx-auto mt-[140px] h-[523px] w-[1400px] overflow-hidden rounded-[24px] text-white">
      <img
        src={mapBannerImage}
        alt={t('contact.map.alt')}
        className="absolute inset-0 object-cover size-full"
      />

      <div className="relative z-10 flex h-full w-[575px] flex-col justify-center pl-[86px]">
        <h2 className="font-expose text-[86px] font-black capitalize leading-[0.994]">
          Travel Far<br />Feel More
        </h2>
        <p className="mt-[24px] whitespace-pre-line font-douyin text-[26px] font-bold leading-[1.4]">
          {t('contact.map.tagline')}
        </p>
      </div>

    </section>
  );
}

const Contact = () => (
  <div className="min-w-[1480px] overflow-hidden bg-white pb-[20px]">
    <ContactHero />
    <ContactForm />
    <MapBanner />
  </div>
);

export default Contact;
